import { useState } from "react"
import { AxiosError } from "axios"
import { AtSign, Loader2 } from "lucide-react"
import { toast } from "sonner"

import { useSendEmailCorrespondence } from "@apis/correspondences/useCorrespondences"
import { ConfirmDialog } from "@components/ConfirmDialog/ConfirmDialog"
import { Button } from "@components/ui/button"
import { useCorrespondenceResolver } from "@components/PlanCorrespondencesTable/utils/useCorrespondenceResolver"

interface TButtonProps {
  account_correspondence_id: number
  account_id: string
  isWelcome: boolean
  correspondence_name: string
}

export const SendEmailButton = ({
  account_correspondence_id,
  account_id,
  isWelcome,
  correspondence_name,
}: TButtonProps) => {
  const [isConfirmOpen, setIsConfirmOpen] = useState(false)
  const { getCorrespondenceId, isCorrespondenceResolverFetching } =
    useCorrespondenceResolver({
      account_id,
      account_correspondence_id,
      isWelcome,
    })
  const sendEmailCorrespondenceMutation = useSendEmailCorrespondence()

  const isSending =
    sendEmailCorrespondenceMutation.isPending || isCorrespondenceResolverFetching

  const handleConfirm = async () => {
    const correspondenceId = await getCorrespondenceId()
    if (!correspondenceId) {
      setIsConfirmOpen(false)
      toast.error("Error fetching welcome kit")
      return
    }

    sendEmailCorrespondenceMutation.mutate(
      {
        account_correspondence_id: correspondenceId,
      },
      {
        onSuccess: () => {
          toast.success("Email successfully Sent!")
        },
        onError: (error: string | AxiosError) =>
          toast.error(`Error sending email! ${error}`),
        onSettled: () => setIsConfirmOpen(false),
      },
    )
  }

  return (
    <>
      <Button
        size="icon"
        variant="ghost"
        onClick={() => setIsConfirmOpen(true)}
        disabled={(!account_correspondence_id && !isWelcome) || isSending}
      >
        {sendEmailCorrespondenceMutation.isPending ? (
          <Loader2 className="tw:animate-spin" />
        ) : (
          <AtSign />
        )}
      </Button>
      <ConfirmDialog
        open={isConfirmOpen}
        onOpenChange={setIsConfirmOpen}
        title="Resend this correspondence by email?"
        description={`This will send ${correspondence_name} to the customer's email.`}
        confirmLabel="Send email"
        isPending={isSending}
        onConfirm={handleConfirm}
      />
    </>
  )
}
